import { useCallback, useMemo } from 'react';

import { getImageUrlsFromContent } from '@/playlist/services/Helper';

import type { GameAnswer, PlayList, Question, SendAnswerRequest } from '@/playlist/types';

export type LaneChooserQuestionDetails = {
	question?: Question;
	answers: GameAnswer[];
	answerLabels: string[];
	answerCorrectFlags: boolean[];
	titleText: string;
	questionHtml: string;
	imageUrls: string[];
	questionHasImage: boolean;
};

export type SubmitAnswerResult = {
	isCorrect: boolean;
	answer: GameAnswer;
	answerIndex: number;
	elapsedSeconds: number;
};

export type UseLaneChooserQuestionParams = {
	question?: Question;
	playList?: PlayList | null;
	questionStartTs: number;
	sendAnswerRequest: SendAnswerRequest;
	freePlayCategoryId?: number;
};

const MAX_ANSWERS = 4;

const normalizeAnswers = (answers?: GameAnswer[] | null): GameAnswer[] => {
	if (!Array.isArray(answers)) return [];
	return answers.filter(Boolean).slice(0, MAX_ANSWERS);
};

const extractQuestionHtml = (question?: Question): string => {
	if (!question) return '';
	const rawDisplay = question.question_display;
	if (typeof rawDisplay === 'string') return rawDisplay;
	if (Array.isArray(rawDisplay)) {
		const first = rawDisplay[0];
		if (typeof first === 'string') return first;
	}
	return question.title || '';
};

export default function useLaneChooserQuestion({
	question,
	playList,
	questionStartTs,
	sendAnswerRequest,
	freePlayCategoryId,
}: UseLaneChooserQuestionParams): {
	details: LaneChooserQuestionDetails;
	submitAnswer: (
		selectedIndex: number,
		callback?: (response?: unknown) => void,
	) => SubmitAnswerResult | null;
} {
	const answers = useMemo(() => normalizeAnswers(question?.answers), [question]);

	const details = useMemo<LaneChooserQuestionDetails>(() => {
		const answerLabels = answers.map((item) => item?.answer_display ?? '');
		const answerCorrectFlags = answers.map((item) => Boolean(item?.is_correct));
		const titleText = String(question?.title ?? '').trim();
		const questionHtml = extractQuestionHtml(question);
		const questionDisplay = question?.question_display;
		const firstElement = Array.isArray(questionDisplay) ? questionDisplay[0] : questionDisplay;
		const imageUrls =
			getImageUrlsFromContent(typeof firstElement === 'string' ? firstElement : '') ?? [];
		const questionHasImage = imageUrls.length > 0;

		return {
			question,
			answers,
			answerLabels,
			answerCorrectFlags,
			titleText,
			questionHtml,
			imageUrls,
			questionHasImage,
		};
	}, [answers, question]);

	const submitAnswer = useCallback(
		(
			selectedIndex: number,
			callback?: (response?: unknown) => void,
		): SubmitAnswerResult | null => {
			const selectedAnswer = answers[selectedIndex];
			const playlistId = playList?.id;
			const questionId = question?.id;
			if (!selectedAnswer || playlistId == null || questionId == null) {
				return null;
			}

			const elapsedSeconds = Math.max(0, Math.round((Date.now() - questionStartTs) / 1000));
			const body = {
				playlist_section_answer: {
					user_learning_path_section_id: playlistId,
					question_id: questionId,
					answer_id: selectedAnswer.id,
					answer_texts: [selectedAnswer.answer_display],
					answer_time_in_seconds: elapsedSeconds,
				},
			};

			const isCorrect = Boolean(selectedAnswer.is_correct);

			sendAnswerRequest({
				playlistId,
				freePlayCategoryId: freePlayCategoryId ?? null,
				data: body,
				lastQuestion: false,
				callback,
				playtimeInSeconds: elapsedSeconds,
				isCorrect,
			});

			return {
				isCorrect,
				answer: selectedAnswer,
				answerIndex: selectedIndex,
				elapsedSeconds,
			};
		},
		[answers, playList, question, questionStartTs, sendAnswerRequest, freePlayCategoryId],
	);

	return { details, submitAnswer };
}
